import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { CircularProgress, Box, Typography } from '@mui/material';

const Portability_Sale_Fps = ({ month, year, schemeId, block, district }) => {
  const [fpsData, setFpsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fpsRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_URL_SALE}/api/portabilitySale/fps`,
          { params: { month, year, district, block, schemeId } }
        );
        setFpsData(response.data?.data || []);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [month, year, district, block, schemeId]);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const monthName = monthNames[month - 1] || '';

  // Dynamically collect all keys for commodities and percentages
  const saleKeys = Array.from(new Set(fpsData.flatMap(item => Object.keys(item.salecommodities_qty || {}))));
  const portKeys = Array.from(new Set(fpsData.flatMap(item => Object.keys(item.portcommodities_qty || {}))));
  const percentKeys = Array.from(new Set(fpsData.flatMap(item => Object.keys(item.percent_port_per_commodity || {}))));

  // Compute totals dynamically
  const totals = fpsData.reduce((acc, item) => {
    const info = item.fpsInfo || {}; // Changed from districtInfo to fpsInfo
    acc.totalCards += info.total_cards || 0;
    acc.saleRc += info.total_sale_rc || 0;
    acc.portSaleRc += info.total_port_rc || 0;

    saleKeys.forEach(k => acc.sale[k] = (acc.sale[k] || 0) + (item.salecommodities_qty?.[k] || 0));
    portKeys.forEach(k => acc.port[k] = (acc.port[k] || 0) + (item.portcommodities_qty?.[k] || 0));

    return acc;
  }, { totalCards: 0, saleRc: 0, portSaleRc: 0, sale: {}, port: {} });

  // overall percent (Port / Sale) for totals and per-commodity percent totals
  const overallPercent = totals.saleRc ? (totals.portSaleRc / totals.saleRc) * 100 : 0;
  const percentTotals = percentKeys.reduce((acc, pk) => {
    // derive a normalized base name from the percent key
    const base = pk.replace(/^percent_/, '').replace(/^port_/, '').replace(/^sale_/, '');

    const findKey = (obj, candidates) => candidates.find(c => obj[c] !== undefined);

    const saleCandidates = [pk, `sale_${base}`, `${base}`, `sale${base}`];
    const portCandidates = [pk, `port_${base}`, `${base}`, `port${base}`];

    const saleKey = findKey(totals.sale, saleCandidates);
    const portKey = findKey(totals.port, portCandidates);

    const saleVal = saleKey ? totals.sale[saleKey] : 0;
    const portVal = portKey ? totals.port[portKey] : 0;

    acc[pk] = saleVal ? (portVal / saleVal) * 100 : 0;
    return acc;
  }, {});

  useEffect(() => {
    if (fpsRef.current) {
      fpsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [fpsData]);

  // ---- Render helpers ----
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Something went wrong";

    return (
      <Typography
        color="error"
        align="center"
        sx={{ mt: 4, fontWeight: 'bold', fontSize: 18 }}
      >
        {errorMessage}
      </Typography>
    );
  }

  if (!fpsData.length) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4, fontWeight: 'bold', fontSize: 18 }}>
        No FPS data found for selected block.
      </Typography>
    );
  }

  return (
    <div style={{ marginBottom: 50 }} ref={fpsRef}>
      <table align="center" width="90%" border={1} style={{ borderCollapse: 'collapse', marginTop: 30 }}>
        <thead>
          <tr className='tableheader'>
            <th colSpan={6 + saleKeys.length + portKeys.length + percentKeys.length}>
              Portability Sale Details - {monthName} '{year} - FPS Level (Block: {block})
            </th>
          </tr>
          <tr className='tableheader'>
            <th rowSpan={2} style={{ textAlign: 'left', paddingLeft: 8 }}>Sl. No</th>
            <th rowSpan={2} style={{ textAlign: 'left' }}>FPS</th>
            <th rowSpan={2} style={{ textAlign: 'right' }}>Total Cards</th>
            <th rowSpan={2} style={{ textAlign: 'right' }}>Sale Cards</th>
            <th rowSpan={2} style={{ textAlign: 'right' }}>Portability Sale Cards</th>
            <th rowSpan={2} style={{ textAlign: 'right' }}>% Port / Sale</th>
            <th colSpan={saleKeys.length} style={{ textAlign: 'center' }}>Total Sale Qty (Kg)</th>
            <th colSpan={portKeys.length} style={{ textAlign: 'center' }}>Portability Sale Qty (Kg)</th>
            <th colSpan={percentKeys.length} style={{ textAlign: 'center' }}>% Port / Sale</th>
          </tr>
          <tr className='tableheader'>
            {saleKeys.map(k => <th key={k}>{k.replace('sale_', '').replace(/_/g, ' ')}</th>)}
            {portKeys.map(k => <th key={k}>{k.replace('port_', '').replace(/_/g, ' ')}</th>)}
            {percentKeys.map(k => <th key={k}>{k.replace(/_/g, ' ')}</th>)}
          </tr>
        </thead>
        <tbody>
          {fpsData.map((item, idx) => {
            const info = item.fpsInfo || {}; // Changed from districtInfo to fpsInfo
            const sale = item.salecommodities_qty || {};
            const port = item.portcommodities_qty || {};
            const percent = item.percent_port_per_commodity || {};

            return (
              <tr key={idx}>
                <td style={{ textAlign: 'left', paddingLeft: 8 }}>{idx + 1}</td>
                <td style={{ textAlign: 'left' }}>
                  {info.fps_id} {/* Changed from dist_name_en to fps_name_en */}
                </td>
                <td style={{ textAlign: 'right' }}>{info.total_cards}</td>
                <td style={{ textAlign: 'right' }}>{info.total_sale_rc}</td>
                <td style={{ textAlign: 'right' }}>{info.total_port_rc}</td>
                <td style={{ textAlign: 'right' }}>{(info.percent_port_against_sale || 0).toFixed(2)}</td>

                {saleKeys.map(k => <td key={k} style={{ textAlign: 'right' }}>{(sale[k] || 0).toFixed(2)}</td>)}
                {portKeys.map(k => <td key={k} style={{ textAlign: 'right' }}>{(port[k] || 0).toFixed(2)}</td>)}
                {percentKeys.map(k => <td key={k} style={{ textAlign: 'right' }}>{(percent[k] || 0).toFixed(2)}</td>)}
              </tr>
            );
          })}

          {/* Totals row */}
          <tr className='tableheader'>
            <td colSpan={2} style={{ textAlign: 'center' }}>Total</td>
            <td style={{ textAlign: 'right' }}>{totals.totalCards}</td>
            <td style={{ textAlign: 'right' }}>{totals.saleRc}</td>
            <td style={{ textAlign: 'right' }}>{totals.portSaleRc}</td>
            <td style={{ textAlign: 'right' }}>{overallPercent.toFixed(2)}</td>
            {saleKeys.map(k => <td key={k} style={{ textAlign: 'right' }}>{(totals.sale[k] || 0).toFixed(2)}</td>)}
            {portKeys.map(k => <td key={k} style={{ textAlign: 'right' }}>{(totals.port[k] || 0).toFixed(2)}</td>)}
            {percentKeys.map(k => <td key={k} style={{ textAlign: 'right' }}>{(percentTotals[k] || 0).toFixed(2)}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default Portability_Sale_Fps;